import React, { useState, useEffect } from 'react';
import { Home, Calendar, BarChart3, Settings } from 'lucide-react';
import ContractManager from './components/ContractManager';
import VacationPlanner from './components/VacationPlanner';
import PointsAnalysis from './components/PointsAnalysis';
import { checkPointAvailability } from './utils/dvcLogic';
import { 
  saveContracts, 
  loadContracts, 
  saveVacations, 
  loadVacations, 
  areCookiesSupported,
  getStorageInfo 
} from './utils/cookieStorage';
import { DVCContract } from './utils/dvcLogic';

function App() {
  const [activeTab, setActiveTab] = useState('contracts');
  const [contracts, setContracts] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [pointsAnalysis, setPointsAnalysis] = useState(null);
  const [storageStatus, setStorageStatus] = useState('');

  // Load data from cookies (with localStorage fallback) on component mount
  useEffect(() => {
    try {
      // Try to load from cookies first
      let loadedContracts = loadContracts();
      let loadedVacations = loadVacations();
      
      // If no cookie data, try localStorage as fallback
      if (loadedContracts.length === 0) {
        const savedContracts = localStorage.getItem('dvc-contracts');
        if (savedContracts) {
          const parsedContracts = JSON.parse(savedContracts);
          // Reconstruct DVCContract instances from localStorage data
          loadedContracts = parsedContracts.map(data => {
            try {
              return new DVCContract(
                data.id,
                data.homeResort,
                data.useYearMonth,
                data.annualPoints,
                data.contractYear,
                data.pointsRemaining || {}
              );
            } catch (error) {
              console.warn('Failed to reconstruct contract from localStorage:', data, error);
              return data;
            }
          });
          
          // Migrate to cookies if available
          if (areCookiesSupported()) {
            saveContracts(loadedContracts);
            console.log('Migrated contracts from localStorage to cookies');
          }
        }
      }
      
      if (loadedVacations.length === 0) {
        const savedVacations = localStorage.getItem('dvc-vacations');
        if (savedVacations) {
          loadedVacations = JSON.parse(savedVacations);
          
          // Migrate to cookies if available
          if (areCookiesSupported()) {
            saveVacations(loadedVacations);
            console.log('Migrated vacations from localStorage to cookies');
          }
        }
      }
      
      setContracts(loadedContracts);
      setVacations(loadedVacations);
      
      // Set storage status
      const cookiesSupported = areCookiesSupported();
      const totalItems = loadedContracts.length + loadedVacations.length;
      
      if (totalItems > 0) {
        setStorageStatus(`Loaded ${totalItems} items from ${cookiesSupported ? 'cookies' : 'local storage'}`);
      } else {
        setStorageStatus(cookiesSupported ? 'Using cookies for storage' : 'Using local storage');
      }
      
      // Log storage info for debugging
      console.log('Storage info:', getStorageInfo());
      
    } catch (error) {
      console.error('Error loading saved data:', error);
    }
  }, []);

  // Save data to cookies (with localStorage fallback) whenever contracts or vacations change
  useEffect(() => {
    if (contracts.length > 0) { // Only save if there are contracts to avoid overwriting with empty array on initial load
      if (areCookiesSupported()) {
        const success = saveContracts(contracts);
        if (success) {
          setStorageStatus('Contracts saved to cookies');
          setTimeout(() => setStorageStatus('Using cookies for storage'), 2000);
        }
      } else {
        // Fallback to localStorage if cookies not supported
        localStorage.setItem('dvc-contracts', JSON.stringify(contracts));
        setStorageStatus('Contracts saved to local storage');
        setTimeout(() => setStorageStatus('Using local storage'), 2000);
      }
    }
  }, [contracts]);

  useEffect(() => {
    if (vacations.length > 0) { // Only save if there are vacations to avoid overwriting with empty array on initial load
      if (areCookiesSupported()) {
        const success = saveVacations(vacations);
        if (success) {
          setStorageStatus('Vacations saved to cookies');
          setTimeout(() => setStorageStatus('Using cookies for storage'), 2000);
        }
      } else {
        // Fallback to localStorage if cookies not supported
        localStorage.setItem('dvc-vacations', JSON.stringify(vacations));
        setStorageStatus('Vacations saved to local storage');
        setTimeout(() => setStorageStatus('Using local storage'), 2000);
      }
    }
  }, [vacations]);

  // Update points analysis when contracts or vacations change
  useEffect(() => {
    if (contracts.length > 0 && vacations.length > 0) {
      try {
        const analysis = checkPointAvailability(contracts, vacations);
        setPointsAnalysis(analysis);
      } catch (error) {
        console.error('Error calculating points analysis:', error);
        setPointsAnalysis(null);
      }
    } else {
      setPointsAnalysis(null);
    }
  }, [contracts, vacations]);

  const tabs = [
    { id: 'contracts', label: 'My Contracts', icon: Home },
    { id: 'planner', label: 'Plan Vacations', icon: Calendar },
    { id: 'analysis', label: 'Points Analysis', icon: BarChart3 }
  ];

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'contracts':
        return <ContractManager contracts={contracts} setContracts={setContracts} />;
      case 'planner':
        return <VacationPlanner contracts={contracts} vacations={vacations} setVacations={setVacations} />;
      case 'analysis':
        return <PointsAnalysis contracts={contracts} vacations={vacations} pointsAnalysis={pointsAnalysis} />;
      default:
        return <ContractManager contracts={contracts} setContracts={setContracts} />;
    }
  };

  return (
    <div className="App">
      <header style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '15px 0',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
      }}>
        {/* Decorative background elements */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '300px',
          height: '300px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
          zIndex: 1
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '-30%',
          left: '-5%',
          width: '200px',
          height: '200px',
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '50%',
          zIndex: 1
        }}></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <h1 style={{
            fontSize: '3.5rem',
            fontWeight: '700',
            margin: '0 0 16px 0',
            textAlign: 'center',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            letterSpacing: '-0.02em'
          }}>
            ✨ DVC Point Planner
          </h1>
          <p style={{
            fontSize: '1.25rem',
            textAlign: 'center',
            margin: '0',
            opacity: '0.9',
            fontWeight: '300',
            maxWidth: '600px',
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: '1.6'
          }}>
            Plan your Disney Vacation Club points across multiple contracts and future vacations
          </p>
        </div>
      </header>

      <div className="container">
        <nav className="nav">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <div
                key={tab.id}
                className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={16} style={{ marginRight: '8px' }} />
                {tab.label}
              </div>
            );
          })}
        </nav>

        <main>
          {renderActiveTab()}
        </main>

        <footer style={{ 
          textAlign: 'center', 
          padding: '40px 0', 
          color: '#666', 
          borderTop: '1px solid #eee',
          marginTop: '40px'
        }}>
          <p>
            <strong>DVC Point Planner</strong> - Unofficial tool for Disney Vacation Club members
          </p>
          <p style={{ fontSize: '14px', marginTop: '8px' }}>
            Points data is estimated and for planning purposes only. 
            Always verify current points charts on the official DVC website.
          </p>
          {storageStatus && (
            <p style={{ fontSize: '12px', marginTop: '8px', color: '#999' }}>
              {storageStatus}
            </p>
          )}
        </footer>
      </div>
    </div>
  );
}

export default App;
